import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Tabs, 
  Card, 
  Descriptions, 
  Button, 
  Spin, 
  message, 
  Table, 
  Typography,
  Tag,
  Space
} from 'antd';
import { TimeUtil } from '../utils/timeUtil';
import { 
  ArrowLeftOutlined, 
  CopyOutlined,
  ConsoleSqlOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import api from '../api/auth';
import { createTerminalTicket } from '../api/terminal';
import config from '../config';
import GenerateExternalLinkModal from '../components/GenerateExternalLinkModal';
import NetworkConfigPanel, { NetConfFieldSpec } from '../components/NetworkConfigPanel';
import { PasswordGenerator } from '../utils/passwordGenerator';
import { useAuthStore } from '../store/authStore';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

interface ServerDetailInfo {
  id: number;
  deviceId: string;
  upSn: string;
  merchantId: number;
  lineStatus: string;
  lineStatusFrp?: string;
  netType: string;
  remark: string;
  localSshPort: number;
  proxySshPort: number;
  addTime: string;
  lastUpdateTime: string;
  systemInfo: any;
  cpuInfo: any;
  memoryInfo: any;
  diskInfo: any;
  ethInfo: any;
  managerNetEth: string;
  manageIp: string;
  provinceId: number;
  cityId: number;
  townId: number;
  returnUrl: string;
}

interface PppConfig {
  id: number;
  pppId: number;
  vlanId: number;
  pppAccount: string;
  pppPassword: string;
  eth: string;
  bandWidth: number;
  pppGetIp: string;
  pppLog: string;
  createTime: string;
  lastUpdate?: string;
}

interface StaticIpConfig {
  id: number;
  ipId: number;
  vlanId: number;
  ipaddr: string;
  netmask: string;
  gateway: string;
  ipv6: number;
  eth: string;
  bandWidth: number;
  ipStatus: string;
  createTime: string;
}

interface NetworkInterface {
  name: string;
  type: string;
  status: string;
  mac: string;
  ipv4: string[] | null;
  ipv6: string[] | null;
  rxRate: number;
  txRate: number;
  lastUpdate?: string;
}

interface NetworkInfo {
  interfaces: NetworkInterface[];
}

// PPP配置可编辑字段（与后端 CreateServerPppDto 对齐）
const PPP_FIELDS: NetConfFieldSpec[] = [
  { key: 'pppId', label: 'PPP ID', type: 'number', required: true, width: 90 },
  { key: 'vlanId', label: 'VLAN ID', type: 'number', width: 90 },
  { key: 'pppAccount', label: 'PPP账号', type: 'text', required: true, width: 160, placeholder: 'ppp-user' },
  { key: 'pppPassword', label: 'PPP密码', type: 'text', required: true, width: 160, placeholder: 'ppp-password' },
  { key: 'eth', label: '网卡', type: 'text', required: true, width: 100, placeholder: 'eth0' },
  { key: 'bandWidth', label: '带宽(Mbps)', type: 'number', width: 110 },
];

// 静态IP配置可编辑字段（与后端 CreateServerStaticIpDto 对齐）
const STATIC_IP_FIELDS: NetConfFieldSpec[] = [
  { key: 'ipId', label: 'IP ID', type: 'number', required: true, width: 80 },
  { key: 'vlanId', label: 'VLAN ID', type: 'number', width: 90 },
  { key: 'ipaddr', label: 'IP地址', type: 'text', required: true, width: 150, placeholder: '192.168.1.100' },
  { key: 'netmask', label: '子网掩码', type: 'text', required: true, width: 150, placeholder: '255.255.255.0' },
  { key: 'gateway', label: '网关', type: 'text', required: true, width: 150, placeholder: '192.168.1.1' },
  { key: 'ipv6', label: 'IPv6', type: 'ipv6', width: 90 },
  { key: 'eth', label: '网卡', type: 'text', required: true, width: 100, placeholder: 'eth0' },
  { key: 'bandWidth', label: '带宽(Mbps)', type: 'number', width: 110 },
];

const ServerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role } = useAuthStore();
  const isViewer = role === 'viewer';
  const [server, setServer] = useState<ServerDetailInfo | null>(null);
  const [pppConfigs, setPppConfigs] = useState<PppConfig[]>([]);
  const [staticIpConfigs, setStaticIpConfigs] = useState<StaticIpConfig[]>([]);
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [externalLinkOpen, setExternalLinkOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchServerDetail();
      fetchPppConfigs();
      fetchStaticIpConfigs();
      fetchNetworkInfo();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 当服务器信息加载完成时，生成当天密码
  useEffect(() => {
    if (server && server.deviceId) {
      generatePassword();
    }
  }, [server]); // eslint-disable-line react-hooks/exhaustive-deps

  const generatePassword = () => {
    if (server && server.deviceId) {
      try {
        const today = new Date();
        const password = PasswordGenerator.generatePassword(today, server.deviceId);
        setCurrentPassword(password);
      } catch (error) {
        console.error('生成密码失败:', error);
        message.error('生成密码失败');
      }
    }
  };

  const copyPassword = () => {
    if (currentPassword) {
      navigator.clipboard.writeText(currentPassword).then(() => {
        message.success('密码已复制到剪贴板');
      }).catch(() => {
        message.error('复制失败');
      });
    }
  };

  const apiOrigin = () => config.API_BASE_URL.replace(/\/api\/?$/i, '');

  const openWebShell = async (deviceId: string) => {
    const token = localStorage.getItem('token');
    if (token) {
      window.open(
        `${apiOrigin()}/api/terminal?device_id=${encodeURIComponent(deviceId)}&token=${encodeURIComponent(token)}`,
        '_blank',
        'noopener,noreferrer',
      );
      return;
    }
    try {
      const { data } = await createTerminalTicket({ device_id: deviceId });
      window.open(data.ssh_url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      message.error('获取 WebShell 链接失败');
    }
  };

  const openDeviceWeb = async (deviceId: string) => {
    const token = localStorage.getItem('token');
    if (token) {
      window.open(
        `${apiOrigin()}/api/terminal?web_id=${encodeURIComponent(deviceId)}&token=${encodeURIComponent(token)}`,
        '_blank',
        'noopener,noreferrer',
      );
      return;
    }
    try {
      const { data } = await createTerminalTicket({ device_id: deviceId });
      window.open(data.web_url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      message.error('获取设备 Web 入口失败');
    }
  };

  const fetchServerDetail = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.get(`/servers/${id}`);
      setServer(response.data);
    } catch (error) {
      console.error('Failed to fetch server detail:', error);
      message.error('获取服务器详情失败');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchPppConfigs = async () => {
    try {
      const response = await api.get(`/servers/${id}/ppp`);
      setPppConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch PPP configs:', error);
      message.error('获取PPP配置失败');
    }
  };

  const fetchStaticIpConfigs = async () => {
    try {
      const response = await api.get(`/servers/${id}/static-ip`);
      setStaticIpConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch static IP configs:', error);
      message.error('获取静态IP配置失败');
    }
  };

  const fetchNetworkInfo = async () => {
    try {
      const response = await api.get(`/servers/${id}/netinfo`);
      setNetworkInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch network info:', error);
      message.error('获取网络信息失败');
    }
  };

  // 配置写入后刷新：PPP 与静态IP 互斥清空，需同时刷新两者及服务器信息（回传地址可能变化）
  const refreshNetConfigs = () => {
    fetchPppConfigs();
    fetchStaticIpConfigs();
    fetchServerDetail(true);
  };


  const pppColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'PPP ID',
      dataIndex: 'pppId',
      key: 'pppId',
      render: (pppId: number) => pppId !== null && pppId !== undefined ? pppId : '-',
    },
    {
      title: 'VLAN ID',
      dataIndex: 'vlanId',
      key: 'vlanId',
      render: (vlanId: number) => vlanId !== null && vlanId !== undefined ? vlanId : '-',
    },
    {
      title: 'PPP账号',
      dataIndex: 'pppAccount',
      key: 'pppAccount',
      render: (account: string) => account || '-',
    },
    {
      title: 'PPP密码',
      dataIndex: 'pppPassword',
      key: 'pppPassword',
      render: (password: string) => password || '-',
    },
    {
      title: '网卡',
      dataIndex: 'eth',
      key: 'eth',
      render: (eth: string) => eth || '-',
    },
    {
      title: '带宽(Mbps)',
      dataIndex: 'bandWidth',
      key: 'bandWidth',
      render: (bandWidth: number) => bandWidth !== null && bandWidth !== undefined ? bandWidth : '-',
    },
    {
      title: '获取IPv4',
      dataIndex: 'pppGetIp',
      key: 'pppGetIp',
      render: (ip: string) => ip || '-',
    },
    {
      title: '获取IPv6',
      dataIndex: 'pppGetIpv6',
      key: 'pppGetIpv6',
      render: (ipv6: string) => ipv6 || '-',
    },
    {
      title: '进程ID',
      dataIndex: 'pid',
      key: 'pid',
      render: (pid: number) => pid !== null && pid !== undefined ? pid : '-',
    },
    {
      title: '链路名称',
      dataIndex: 'linkname',
      key: 'linkname',
      render: (linkname: string) => linkname || '-',
    },
    {
      title: 'PPP日志',
      dataIndex: 'pppLog',
      key: 'pppLog',
      render: (log: string) => log || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (date: string) => date ? TimeUtil.formatServerTime(date) : '-',
    },
    {
      title: '最后更新时间',
      dataIndex: 'lastUpdate',
      key: 'lastUpdate',
      render: (date: string) => date ? TimeUtil.formatServerTime(date) : '-',
    },
  ];

  const staticIpColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'IP ID',
      dataIndex: 'ipId',
      key: 'ipId',
    },
    {
      title: 'VLAN ID',
      dataIndex: 'vlanId',
      key: 'vlanId',
    },
    {
      title: 'IP地址',
      dataIndex: 'ipaddr',
      key: 'ipaddr',
    },
    {
      title: '子网掩码',
      dataIndex: 'netmask',
      key: 'netmask',
    },
    {
      title: '网关',
      dataIndex: 'gateway',
      key: 'gateway',
    },
    {
      title: 'IPv6',
      dataIndex: 'ipv6',
      key: 'ipv6',
      render: (ipv6: number) => (
        <Tag color={ipv6 === 1 ? 'green' : 'red'}>
          {ipv6 === 1 ? '开启' : '关闭'}
        </Tag>
      ),
    },
    {
      title: '网卡',
      dataIndex: 'eth',
      key: 'eth',
    },
    {
      title: '带宽(Mbps)',
      dataIndex: 'bandWidth',
      key: 'bandWidth',
    },
    {
      title: '状态',
      dataIndex: 'ipStatus',
      key: 'ipStatus',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status || '-'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (date: string) => date ? TimeUtil.formatServerTime(date) : '-',
    },
  ];

  const networkColumns = [
    {
      title: '接口名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'up' ? 'green' : 'red'}>
          {status === 'up' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: 'MAC地址',
      dataIndex: 'mac',
      key: 'mac',
    },
    {
      title: 'IPv4地址',
      dataIndex: 'ipv4',
      key: 'ipv4',
      render: (ipv4: string[] | null) => {
        if (!ipv4 || ipv4.length === 0) return '-';
        return ipv4.join(', ');
      },
    },
    {
      title: 'IPv6地址',
      dataIndex: 'ipv6',
      key: 'ipv6',
      render: (ipv6: string[] | null) => {
        if (!ipv6 || ipv6.length === 0) return '-';
        return ipv6.join(', ');
      },
    },
    {
      title: '接收速率(bytes/s)',
      dataIndex: 'rxRate',
      key: 'rxRate',
      render: (rate: number) => rate.toLocaleString(),
    },
    {
      title: '发送速率(bytes/s)',
      dataIndex: 'txRate',
      key: 'txRate',
      render: (rate: number) => rate.toLocaleString(),
    },
    {
      title: '最后更新时间',
      dataIndex: 'lastUpdate',
      key: 'lastUpdate',
      render: (date: string) => date ? TimeUtil.formatServerTime(date) : '-',
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!server) {
    return (
      <div>
        <Title level={2}>服务器不存在</Title>
        <Button type="primary" onClick={() => navigate('/servers')}>
          返回服务器列表
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/servers')}
          style={{ marginRight: 8 }}
        >
          返回列表
        </Button>
      </div>
      
      <Title level={2}>服务器详情: {server.deviceId}</Title>
      
      <Card>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="设备ID">{server.deviceId}</Descriptions.Item>
          <Descriptions.Item label="MQTT状态">
            <Tag color={server.lineStatus === 'online' ? 'green' : 'red'}>
              {server.lineStatus === 'online' ? '在线' : '离线'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="FRP状态">
            <Tag color={(server.lineStatusFrp || 'offline') === 'online' ? 'green' : 'red'}>
              {(server.lineStatusFrp || 'offline') === 'online' ? '在线' : '离线'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="设备密码">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'monospace' }}>
                {currentPassword || '生成中...'}
              </span>
              <Button
                icon={<CopyOutlined />}
                size="small"
                onClick={copyPassword}
                disabled={!currentPassword}
                title="复制密码"
              />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="远程终端">
            {isViewer ? (
              <Text type="secondary">只读角色，无终端权限</Text>
            ) : (
            <Space size="small">
              <Button 
                type="primary" 
                icon={<ConsoleSqlOutlined />} 
                size="small"
                onClick={() => void openWebShell(server.deviceId)}
              >
                WebShell
              </Button>
              <Button 
                type="primary" 
                icon={<ConsoleSqlOutlined />} 
                size="small"
                onClick={() => void openDeviceWeb(server.deviceId)}
              >
                设备配置
              </Button>
              <Button
                type="default"
                icon={<LinkOutlined />}
                size="small"
                onClick={() => setExternalLinkOpen(true)}
              >
                生成外部链接
              </Button>
            </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="备注">{server.remark || '-'}</Descriptions.Item>
          <Descriptions.Item label="添加时间">{TimeUtil.formatServerTime(server.addTime)}</Descriptions.Item>
          <Descriptions.Item label="最后更新">{TimeUtil.formatServerTime(server.lastUpdateTime)}</Descriptions.Item>
          <Descriptions.Item label="回传地址">
            {server.returnUrl ? (
              <a href={server.returnUrl} target="_blank" rel="noopener noreferrer">
                {server.returnUrl}
              </a>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          {server.systemInfo && (
            <Descriptions.Item label="系统信息">
              {JSON.stringify(server.systemInfo)}
            </Descriptions.Item>
          )}
          {server.cpuInfo && (
            <Descriptions.Item label="CPU信息">
              {JSON.stringify(server.cpuInfo)}
            </Descriptions.Item>
          )}
          {server.memoryInfo && (
            <Descriptions.Item label="内存信息">
              {JSON.stringify(server.memoryInfo)}
            </Descriptions.Item>
          )}
          {server.diskInfo && (
            <Descriptions.Item label="磁盘信息">
              {JSON.stringify(server.diskInfo)}
            </Descriptions.Item>
          )}
          {server.ethInfo && (
            <Descriptions.Item label="网卡信息">
              {JSON.stringify(server.ethInfo)}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
        
      <Tabs defaultActiveKey="network">
        <TabPane tab="网络信息" key="network">
          <Table 
            columns={networkColumns} 
            dataSource={networkInfo?.interfaces || []} 
            rowKey={(record, index) => index || 0}
            pagination={false}
          />
        </TabPane>
        
        <TabPane tab="PPP配置" key="ppp">
          <NetworkConfigPanel
            deviceId={server.deviceId}
            kind="ppp"
            configs={pppConfigs}
            columns={pppColumns as any}
            fields={PPP_FIELDS}
            idKey="pppId"
            otherCount={staticIpConfigs.length}
            otherLabel="静态IP"
            returnUrl={server.returnUrl}
            readonly={isViewer}
            onRefresh={refreshNetConfigs}
          />
        </TabPane>
        
        <TabPane tab="静态IP配置" key="staticIp">
          <NetworkConfigPanel
            deviceId={server.deviceId}
            kind="staticIp"
            configs={staticIpConfigs}
            columns={staticIpColumns as any}
            fields={STATIC_IP_FIELDS}
            idKey="ipId"
            otherCount={pppConfigs.length}
            otherLabel="PPP"
            returnUrl={server.returnUrl}
            readonly={isViewer}
            onRefresh={refreshNetConfigs}
          />
        </TabPane>
      </Tabs>

      <GenerateExternalLinkModal
        open={externalLinkOpen}
        deviceId={server.deviceId}
        onClose={() => setExternalLinkOpen(false)}
      />
    </div>
  );
};

export default ServerDetail;
